import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container"]

  connect() {
    this.currentZoom = 1
    this.autoScale = true // Enable auto-scaling

    // Listen for activity list changes
    this.boundHandleActivityListChanged = this.handleActivityListChanged.bind(this)
    window.addEventListener('activity-list-changed', this.boundHandleActivityListChanged)

    // Auto-scale on load and resize
    this.boundAutoScale = this.handleAutoScale.bind(this)
    this.handleAutoScale()
    window.addEventListener('resize', this.boundAutoScale)

    // Use ResizeObserver for container changes
    if (this.hasContainerTarget) {
      this.resizeObserver = new ResizeObserver(this.boundAutoScale)
      this.resizeObserver.observe(this.containerTarget)
    }

    // Draw phase connections for both single and multi-phase layouts
    this.boundDrawPhaseConnections = this.drawPhaseConnections.bind(this)
    this.boundDrawMultiPhaseConnections = this.drawMultiPhaseConnections.bind(this)

    // Initial draw - use requestAnimationFrame for immediate rendering
    requestAnimationFrame(() => {
      requestAnimationFrame(this.boundDrawPhaseConnections)
      requestAnimationFrame(this.boundDrawMultiPhaseConnections)
    })

    // Only redraw on actual window resize, not on zoom changes
    window.addEventListener('resize', this.boundDrawPhaseConnections)
    window.addEventListener('resize', this.boundDrawMultiPhaseConnections)

    // Use IntersectionObserver to detect when map becomes visible (tab switch)
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Map became visible - reset zoom and redraw
          this.currentZoom = 1
          this.autoScale = true

          const container = document.getElementById('single-phase-container')
          if (container) {
            container.style.transform = 'none'
          }

          // Redraw after resetting zoom
          setTimeout(() => {
            requestAnimationFrame(() => {
              requestAnimationFrame(this.boundDrawPhaseConnections)
              requestAnimationFrame(this.boundDrawMultiPhaseConnections)
            })
          }, 200)
        }
      })
    }, { threshold: 0.1 })

    if (this.hasContainerTarget) {
      this.observer.observe(this.containerTarget)
    }
  }

  disconnect() {
    window.removeEventListener('activity-list-changed', this.boundHandleActivityListChanged)
    window.removeEventListener('resize', this.boundAutoScale)
    window.removeEventListener('resize', this.boundDrawPhaseConnections)
    window.removeEventListener('resize', this.boundDrawMultiPhaseConnections)
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  async handleActivityListChanged() {
    // Re-render map when activities are added or removed
    console.log('Activity list changed event received')

    // Wait a bit for the DOM to settle after the activity change
    setTimeout(async () => {
      console.log('Fetching updated activity map...')

      try {
        // Get the inloop ID from the URL
        const pathParts = window.location.pathname.split('/')
        const inloopId = pathParts[pathParts.indexOf('inloops') + 1]

        console.log('Inloop ID:', inloopId)

        if (!inloopId) {
          console.log('No inloop ID found in URL')
          return
        }

        // Fetch the activity map partial
        const url = `/facilitator/inloops/${inloopId}/activity_map`
        console.log('Fetching:', url)

        const response = await fetch(url, {
          headers: {
            'Accept': 'text/html',
            'X-Requested-With': 'XMLHttpRequest'
          }
        })

        console.log('Response status:', response.status)

        if (response.ok) {
          const html = await response.text()
          console.log('Received HTML length:', html.length)

          const parser = new DOMParser()
          const doc = parser.parseFromString(html, 'text/html')
          const newContainer = doc.querySelector('[data-activity-map-target="container"]')

          console.log('Found new container:', !!newContainer)
          console.log('Has container target:', this.hasContainerTarget)

          if (newContainer && this.hasContainerTarget) {
            // Update the container content
            this.containerTarget.innerHTML = newContainer.innerHTML
            console.log('Activity map updated successfully')

            // Redraw phase connections after DOM update - use requestAnimationFrame for speed
            requestAnimationFrame(() => {
              requestAnimationFrame(this.boundDrawPhaseConnections)
              requestAnimationFrame(this.boundDrawMultiPhaseConnections)
            })
          }
        } else {
          console.error('Failed to fetch activity map:', response.statusText)
        }
      } catch (error) {
        console.error('Error updating activity map:', error)
      }
    }, 200)
  }

  zoomIn() {
    this.currentZoom = Math.min(this.currentZoom + 0.2, 3)
    this.applyZoom()
  }

  zoomOut() {
    this.currentZoom = Math.max(this.currentZoom - 0.2, 0.5)
    this.applyZoom()
  }

  resetZoom() {
    this.currentZoom = 1
    this.applyZoom()
  }

  applyZoom() {
    if (!this.hasContainerTarget) return

    const content = this.containerTarget.querySelector('.flex')
    if (!content) return

    // Apply zoom to the entire single-phase-container so canvas scales with content
    const container = document.getElementById('single-phase-container')
    if (container) {
      container.style.transform = `scale(${this.currentZoom})`
      container.style.transformOrigin = 'center center'
      container.style.transition = 'transform 0.3s ease'
    } else {
      // For multi-phase layout, apply zoom to content only
      content.style.transform = `scale(${this.currentZoom})`
      content.style.transformOrigin = 'center center'
      content.style.transition = 'transform 0.3s ease'
    }

    // DON'T redraw canvas on zoom - let it scale with the container
  }

  handleAutoScale() {
    if (!this.autoScale || !this.hasContainerTarget) return

    const content = this.containerTarget.querySelector('.flex')
    if (!content) return

    // Get container and content dimensions
    const containerWidth = this.containerTarget.clientWidth
    const contentWidth = content.scrollWidth

    // Calculate scale to fit content in container (with padding)
    const padding = 64 // Account for padding
    const availableWidth = containerWidth - padding

    if (contentWidth > availableWidth) {
      const scale = availableWidth / contentWidth
      this.currentZoom = Math.max(0.5, Math.min(1, scale)) // Limit between 0.5 and 1
    } else {
      this.currentZoom = 1 // Reset to normal if fits
    }

    this.applyZoom()
  }

  zoomIn() {
    this.autoScale = false // Disable auto-scale when manually zooming
    this.currentZoom = Math.min(this.currentZoom + 0.2, 3)
    this.applyZoom()
  }

  zoomOut() {
    this.autoScale = false // Disable auto-scale when manually zooming
    this.currentZoom = Math.max(this.currentZoom - 0.2, 0.5)
    this.applyZoom()
  }

  resetZoom() {
    this.autoScale = true // Re-enable auto-scale on reset
    this.handleAutoScale()
  }

  drawPhaseConnections() {
    const canvas = document.getElementById('phase-connections-canvas')
    const loadingEl = document.getElementById('canvas-loading')

    if (!canvas) {
      return
    }

    // Show loading
    if (loadingEl) loadingEl.style.display = 'flex'

    // Add delay to ensure DOM is fully rendered before calculating positions
    setTimeout(() => {
      const container = document.getElementById('single-phase-container')
      const phaseBox = document.getElementById('phase-box')
      const activityBoxes = document.querySelectorAll('.activity-box')

      if (!container || !phaseBox || activityBoxes.length === 0) {
        if (loadingEl) loadingEl.style.display = 'none'
        return
      }

      // CRITICAL: Temporarily remove transform to get accurate measurements
      const savedTransform = container.style.transform
      container.style.transform = 'none'

      // Force reflow to ensure measurements are accurate
      container.offsetHeight

      // Get container position for offset calculation
      const containerRect = container.getBoundingClientRect()

      // Validate dimensions
      if (!containerRect.width || !containerRect.height) {
        if (loadingEl) loadingEl.style.display = 'none'
        return
      }

      console.log('Container rect:', {
        left: containerRect.left,
        top: containerRect.top,
        width: containerRect.width,
        height: containerRect.height
      })

      // Set canvas size
      canvas.width = containerRect.width
      canvas.height = containerRect.height

      const ctx = canvas.getContext('2d')
      if (!ctx) {
        if (loadingEl) loadingEl.style.display = 'none'
        return
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // Get phase box position relative to container using getBoundingClientRect
      const phaseRect = phaseBox.getBoundingClientRect()
      const containerOffsetX = containerRect.left
      const containerOffsetY = containerRect.top

      console.log('Phase rect:', {
        left: phaseRect.left,
        bottom: phaseRect.bottom,
        width: phaseRect.width
      })

      // Start point: bottom center of phase box (relative to container)
      const startX = phaseRect.left + phaseRect.width / 2 - containerOffsetX
      const startY = phaseRect.bottom - containerOffsetY

      console.log('Start point:', { startX, startY })

      // Validate start coordinates
      if (isNaN(startX) || isNaN(startY)) {
        if (loadingEl) loadingEl.style.display = 'none'
        return
      }

      // Calculate horizontal line position (midpoint between phase and activities)
      const firstActivityRect = activityBoxes[0].getBoundingClientRect()
      const firstActivityY = firstActivityRect.top - containerOffsetY
      const horizontalY = startY + (firstActivityY - startY) / 2

      console.log('First activity rect:', {
        left: firstActivityRect.left,
        top: firstActivityRect.top
      })
      console.log('Horizontal Y:', horizontalY)

      // Draw vertical line from phase down to horizontal line
      ctx.beginPath()
      ctx.moveTo(startX, startY)
      ctx.lineTo(startX, horizontalY)
      ctx.strokeStyle = '#374151'
      ctx.lineWidth = 2
      ctx.stroke()

      // Calculate leftmost and rightmost activity positions for horizontal line
      let minX = Infinity
      let maxX = -Infinity

      activityBoxes.forEach((activityBox) => {
        const activityRect = activityBox.getBoundingClientRect()
        const centerX = activityRect.left + activityRect.width / 2 - containerOffsetX
        minX = Math.min(minX, centerX)
        maxX = Math.max(maxX, centerX)
      })

      // Draw horizontal line connecting all activities
      ctx.beginPath()
      ctx.moveTo(minX, horizontalY)
      ctx.lineTo(maxX, horizontalY)
      ctx.strokeStyle = '#374151'
      ctx.lineWidth = 2
      ctx.stroke()

      // Draw vertical lines down to each activity
      activityBoxes.forEach((activityBox) => {
        const activityRect = activityBox.getBoundingClientRect()
        const endX = activityRect.left + activityRect.width / 2 - containerOffsetX
        const endY = activityRect.top - containerOffsetY

        // Validate end coordinates
        if (isNaN(endX) || isNaN(endY)) return

        // Draw vertical line from horizontal line to activity
        ctx.beginPath()
        ctx.moveTo(endX, horizontalY)
        ctx.lineTo(endX, endY)
        ctx.strokeStyle = '#374151'
        ctx.lineWidth = 2
        ctx.stroke()

        // Draw arrowhead pointing down at activity
        const arrowLength = 8
        ctx.beginPath()
        ctx.moveTo(endX, endY)
        ctx.lineTo(endX - arrowLength / 2, endY - arrowLength)
        ctx.lineTo(endX + arrowLength / 2, endY - arrowLength)
        ctx.closePath()
        ctx.fillStyle = '#374151'
        ctx.fill()
      })

      // Restore the transform after measurements
      container.style.transform = savedTransform

      // Hide loading after drawing
      if (loadingEl) {
        setTimeout(() => {
          loadingEl.style.display = 'none'
        }, 100)
      }
    }, 150) // Increased delay to allow DOM rendering and transform calculations
  }

  visitActivity({ params: { url, inloopId, activityId } }) {
    if (url) {
      const persistKey = `inloop-${inloopId}-activity-${activityId}-tab`
      localStorage.setItem(persistKey, 'configuration')
      window.location.href = url
    }
  }

  drawMultiPhaseConnections() {
    const canvas = document.getElementById('multi-phase-connections-canvas')
    const container = document.getElementById('multi-phase-container')

    if (!canvas || !container) {
      return
    }

    const phaseBoxes = document.querySelectorAll('.phase-box-multi')

    if (phaseBoxes.length < 2) {
      return // Need at least 2 phases to draw connections
    }

    // Add delay to ensure DOM is fully rendered before calculating positions
    setTimeout(() => {
      // Force reflow to ensure accurate measurements
      container.offsetHeight

      // Get container position for offset calculation
      const containerRect = container.getBoundingClientRect()

      if (!containerRect.width || !containerRect.height) {
        return
      }

      // Validate dimensions
      console.log('Multi-phase container rect:', {
        left: containerRect.left,
        top: containerRect.top,
        width: containerRect.width,
        height: containerRect.height
      })

      // Set canvas size to match container
      canvas.width = containerRect.width
      canvas.height = containerRect.height

      const ctx = canvas.getContext('2d')
      if (!ctx) {
        return
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height)

      const containerOffsetX = containerRect.left
      const containerOffsetY = containerRect.top

      // Draw arrows between consecutive phase boxes
      phaseBoxes.forEach((phaseBox, index) => {
        if (index < phaseBoxes.length - 1) {
          const currentRect = phaseBox.getBoundingClientRect()
          const nextRect = phaseBoxes[index + 1].getBoundingClientRect()

          console.log(`Phase ${index} rect:`, {
            left: currentRect.left,
            right: currentRect.right,
            top: currentRect.top,
            height: currentRect.height
          })

          // Start point: at right edge center of current phase box (account for border)
          const startX = currentRect.right - containerOffsetX - 1
          const startY = currentRect.top + currentRect.height / 2 - containerOffsetY

          // End point: at left edge center of next phase box (account for border)
          const endX = nextRect.left - containerOffsetX + 1
          const endY = nextRect.top + nextRect.height / 2 - containerOffsetY

          console.log(`Arrow ${index}:`, { startX, startY, endX, endY })

          // Validate coordinates
          if (isNaN(startX) || isNaN(startY) || isNaN(endX) || isNaN(endY)) {
            console.warn('Invalid coordinates for arrow', index)
            return
          }

          const arrowLength = 8

          // Draw line from start to just before the arrowhead
          ctx.beginPath()
          ctx.moveTo(startX, startY)
          ctx.lineTo(endX - arrowLength, endY) // Stop before arrowhead
          ctx.strokeStyle = '#374151'
          ctx.lineWidth = 2
          ctx.stroke()

          // Draw arrowhead pointing right, tip touches the box edge
          ctx.beginPath()
          ctx.moveTo(endX, endY) // Arrow tip at box edge
          ctx.lineTo(endX - arrowLength, endY - arrowLength / 2)
          ctx.lineTo(endX - arrowLength, endY + arrowLength / 2)
          ctx.closePath()
          ctx.fillStyle = '#374151'
          ctx.fill()
        }
      })
    }, 200) // Increased delay to allow DOM rendering and ensure accurate measurements
  }
}
